import { useState, useCallback, useEffect, useContext } from "react";
import { toast, Zoom } from "react-toastify";
import {
  debounce,
  getRCAccountId,
  getUserData,
  queryStringBuilder,
} from "@client/utils";
import EditNotificationView from "./view";
import { useNavigate, useParams } from "react-router-dom";
import Service from "../service";
import useStyles from "./style";
import {
  DEFAULT_ERROR_MESSAGE,
  DEFAULT_WAIT_MESSAGE,
  DELIVERY_TYPE,
} from "@client/shared/constants";
import { AppContext } from "@client/shared/contexts";
import PageLoader from "../../../shared/components/page-loader";
import { AppService } from "@client/shared/services";

const updatePreferences = debounce(
  async ({
    duplicate,
    duplicateSuccess,
    bulkEdit,
    notificationEdit,
    delete: deleteNotification,
    toggle,
  }) => {
    const { error } = await Service.updatePreferences({
      duplicate_notification: duplicate,
      notification_duplicated: duplicateSuccess,
      confirm_bulk_edit: bulkEdit,
      notification_edited: notificationEdit,
      delete_notification: deleteNotification,
      toggle_notification: toggle,
    });
    if (error) {
      toast.error("Failed to update preference.");
    }
  },
  1000,
);

const defaultState = {
  dialog: {
    delete: false,
    duplicate: false,
    duplicateSuccess: false,
    isDuplicating: false,
    toggle: false,
  },
  isSaving: false,
  notification: {},
  preference: {
    delete: false,
    duplicate: false,
    duplicateSuccess: false,
    toggle: false,
  },
  userHasChanged: false,
  values: null,
  isParsingPhoneNumber: false,
  isPhoneNumberParsed: true,
};

const EditNotification = () => {
  const [state, setState] = useState(defaultState);

  const userData = (getUserData() || {}).user || {};
  const { appData, setAppData } = useContext(AppContext);
  const params = useParams();
  const navigate = useNavigate();

  const isSectionComplete = (section) => {
    let isComplete = true;
    const currentValues = state.values;
    if (!currentValues) {
      return false;
    }

    switch (section) {
      case 0:
        return !!currentValues.name && currentValues.name.length < 101;
      case 1:
        const externalNumber =
          currentValues.externalNumber === "+1"
            ? null
            : currentValues.externalNumber;
        return currentValues.isExtensionNumber
          ? !!currentValues.extensionNumber &&
              !currentValues.extensionNumberError
          : (!!externalNumber &&
              !currentValues.externalNumberError &&
              state.isPhoneNumberParsed) ||
              !!currentValues.voicemail_extensions;
      case 2:
        return !!currentValues.callDirection;
      case 3:
        isComplete = true;
        if (!!currentValues.voicemail_extensions) {
          return isComplete;
        }
        if (
          !currentValues.switches.users &&
          !currentValues.switches.extensionPattern &&
          !currentValues.switches.siteExtensions
        ) {
          return false;
        }

        if (currentValues.switches.users) {
          if (!currentValues.switches.allUsers) {
            if (!currentValues.triggers.users.length) {
              isComplete = false;
            }
          }
        }

        if (currentValues.switches.extensionPattern) {
          if (
            !currentValues.triggers.extensionPattern.length ||
            currentValues.triggers.extensionPattern.length > 300
          ) {
            isComplete = false;
          }
        }

        if (currentValues.switches.siteExtensions) {
          if (!currentValues.triggers.sites.length) {
            isComplete = false;
          }
        }

        return isComplete;
      case 4:
        isComplete = true;
        if (!currentValues.switches.sendImmediate) {
          const isMessageRequired = (currentValues.deliverySteps || [])
            .filter((item) => !item.isDeleted)
            .some((item) => {
              return (
                item.type === DELIVERY_TYPE.EMAIL ||
                item.type === DELIVERY_TYPE.SMS ||
                item.type === DELIVERY_TYPE.GLIP
              );
            });

          if (!currentValues.delivery.message && isMessageRequired) {
            return false;
          }
          return (currentValues.deliverySteps || [])
            .filter((item) => !item.isDeleted)
            .some((item) => {
              return (
                item.type === DELIVERY_TYPE.EMAIL ||
                item.type === DELIVERY_TYPE.SMS ||
                item.type === DELIVERY_TYPE.WEBHOOK ||
                item.type === DELIVERY_TYPE.GLIP ||
                item.type === DELIVERY_TYPE.RINGOUT
              );
            });
        }
        if (
          !currentValues.switches.email &&
          !currentValues.switches.webhook &&
          !currentValues.switches.ringCentralApp &&
          !currentValues.switches.sms &&
          !currentValues.switches.ringout
        ) {
          return false;
        }

        if (currentValues.switches.email) {
          if (
            !currentValues.delivery.email ||
            currentValues.delivery.email.length > 500
          ) {
            isComplete = false;
          }
        }

        // if (currentValues.switches.webhook) {
        //   if (
        //     !currentValues.delivery.webhook.length ||
        //     currentValues.delivery.webhook.length > 500 ||
        //     currentValues.delivery.isJsonError
        //   ) {
        //     isComplete = false;
        //   }
        // }
        if (currentValues.switches.webhook) {
          if (!currentValues.delivery.webhooks.length) {
            isComplete = false;
          }
        }

        if (currentValues.switches.ringCentralApp) {
          if (!currentValues.delivery.ringCentralTeams.length) {
            isComplete = false;
          }
        }

        if (currentValues.switches.sms) {
          if (
            !currentValues.delivery.sms ||
            currentValues.delivery.sms.length > 500
          ) {
            isComplete = false;
          }
        }

        const isMessageOptional =
          !currentValues.switches.email &&
          !currentValues.switches.ringCentralApp &&
          !currentValues.switches.sms;

        if (!currentValues.delivery.message && !isMessageOptional) {
          isComplete = false;
        }

        if (currentValues.switches.ringout) {
          if (
            !currentValues.delivery.ringout?.extension ||
            !currentValues.delivery.ringout?.extensionPhoneNumber ||
            !currentValues.delivery.ringout?.phoneNumber ||
            !!(currentValues.delivery.ringout?.phoneNumberError || "").trim()
          ) {
            isComplete = false;
          }
        }

        return isComplete;
      default:
        return (
          isSectionComplete(0) &&
          isSectionComplete(1) &&
          isSectionComplete(2) &&
          isSectionComplete(3) &&
          isSectionComplete(4)
        );
    }
  };

  // Fetchers

  const fetchNotification = async () => {
    const query = `?filter[where][id]=${params.id}`;
    const { data, error } = await Service.get(null, query);
    if (error) {
      toast.error(Array.isArray(error) ? error[0] && error[0].message : error);
    } else {
      setState((prevState) => ({
        ...prevState,
        notification: data.notifications[0] || {},
      }));
    }
  };

  // const fetchUserPreferences = useCallback(async () => {
  //   const { data, error } = await Service.getUserDetails();
  //   if (error) {
  //     toast.error(Array.isArray(error) ? error[0] && error[0].message : error);
  //   } else {
  //     setState((prevState) => ({
  //       ...prevState,
  //       needsAcknowledgement: !data.user.regulations_911_acknowledgement,
  //       preference: {
  //         delete: data.user.user_preference.delete_notification,
  //         duplicate: data.user.user_preference.duplicate_notification,
  //         duplicateSuccess: data.user.user_preference.notification_duplicated,
  //         bulkEdit: data.user.user_preference.confirm_bulk_edit,
  //         notificationEdit: data.user.user_preference.notification_edited,
  //         toggle: data.user.user_preference.toggle_notification,
  //       },
  //     }));
  //   }
  // }, []);

  // Lifecycle

  useEffect(() => {
    fetchNotification();
    // fetchUserPreferences();
  }, []);

  // Handlers

  const handleDeleteConfirmation = async (id = "") => {
    // debounce(async () => {
    toast.info("Deleting notification.", {
      autoClose: 2000,
    });
    setState((prevState) => ({
      ...prevState,
      dialog: {
        ...prevState.dialog,
        isDeleting: true,
      },
    }));
    const query = `?ids=${id}`;
    const { error } = await Service.deleteNotification(query);
    if (error) {
      toast.error(Array.isArray(error) ? error[0] && error[0].message : error);
    } else {
      toast.success("Deleted successfully");
      navigate("/notifications");
    }
    // }, 600);
    setState((prevState) => ({
      ...prevState,
      dialog: {
        ...prevState.dialog,
        isDeleting: false,
      },
    }));
  };

  const handleDuplicateConfirmation = useCallback(async () => {
    toast.info("Duplicating notification.", {
      autoClose: 2000,
    });
    setState((prevState) => ({
      ...prevState,
      dialog: {
        ...prevState.dialog,
        isDuplicating: true,
      },
    }));
    // let queryString = queryStringBuilder(0, 0, [], filters, sortObj);
    const query = `?ids=${params.id}`;
    const { error } = await Service.duplicateNotification(query);
    if (error) {
      toast.error(Array.isArray(error) ? error[0] && error[0].message : error);
      setState((prevState) => ({
        ...prevState,
        dialog: {
          ...prevState.dialog,
          isDuplicating: false,
        },
      }));
    } else {
      setState((prevState) => {
        if (
          !prevState.dialog.duplicate ||
          !prevState.preference.duplicateSuccess
        ) {
          toast.success("Duplicated successfully");
          return {
            ...prevState,
            dialog: {
              ...prevState.dialog,
              duplicate: false,
              isDuplicating: false,
            },
          };
        } else {
          return {
            ...prevState,
            dialog: {
              ...prevState.dialog,
              duplicate: false,
              duplicateSuccess: true,
              isDuplicating: false,
            },
          };
        }
      });
    }
  }, []);

  const handleDuplicateClick = useCallback(() => {
    setState((prevState) => {
      if (!prevState.preference.duplicate) {
        return {
          ...prevState,
          dialog: {
            ...prevState.dialog,
            duplicate: true,
          },
        };
      } else {
        handleDuplicateConfirmation();
        return prevState;
      }
    });
  }, [handleDuplicateConfirmation]);

  const handleDelete = () => {
    if (!state.preference.delete) {
      setState((prevState) => ({
        ...prevState,
        dialog: {
          ...prevState.dialog,
          delete: true,
        },
      }));
    } else {
      handleDeleteConfirmation(params.id);
    }
  };

  const notificationFormOnChange = (event, userHasChanged) => {
    setState((prevState) => ({
      ...prevState,
      values: event,
      userHasChanged,
    }));
  };
  const onParsingPhoneNumber = (value, userHasChanged) => {
    setState((prevState) => ({
      ...prevState,
      isParsingPhoneNumber: value,
      userHasChanged,
    }));
  };

  const onPhoneNumParsedSuccess = (value) => {
    setState((prevState) => ({
      ...prevState,
      isPhoneNumberParsed: value,
    }));
  };

  const deleteOnCancel = (event) => {
    setState((prevState) => ({
      ...prevState,
      dialog: {
        ...prevState.dialog,
        delete: false,
      },
    }));
  };
  const duplicateOnCancel = (event) => {
    setState((prevState) => ({
      ...prevState,
      dialog: {
        ...prevState.dialog,
        duplicate: false,
      },
    }));
  };
  const deleteOnPreferenceChange = (event) => {
    setState((prevState) => {
      const updatedPreferences = {
        ...prevState.preference,
        delete: event.currentTarget.checked,
      };

      updatePreferences(updatedPreferences);
      return {
        ...prevState,
        preference: updatedPreferences,
      };
    });
  };

  const duplicateOnPreferenceChange = (event) => {
    setState((prevState) => {
      const updatedPreferences = {
        ...prevState.preference,
        duplicate: event.currentTarget.checked,
      };

      updatePreferences(updatedPreferences);
      return {
        ...prevState,
        preference: updatedPreferences,
      };
    });
  };

  const duplicateOnClose = (event) => {
    setState((prevState) => ({
      ...prevState,
      dialog: {
        ...prevState.dialog,
        duplicateSuccess: false,
      },
    }));
  };

  const DuplicateOnConfirm = (event) => {
    setState((prevState) => ({
      ...prevState,
      dialog: {
        ...prevState.dialog,
        duplicateSuccess: false,
      },
    }));
  };
  const handleDuplicateSuccess = (event) => {
    setState((prevState) => {
      const updatedPreferences = {
        ...prevState.preference,
        duplicateSuccess: !event.currentTarget.checked,
      };

      updatePreferences(updatedPreferences);
      return {
        ...prevState,
        preference: updatedPreferences,
      };
    });
  };

  function getSkippedElements(elements, startIndex, endIndex) {
    return elements
      .slice(startIndex, endIndex)
      .filter((element) => element.isDeleted).length;
  }

  const handleFormSubmit = async () => {
    const { values } = state;
    const hasDeliveryVoiceRead = (values.deliverySteps || []).some(
      (item) => item.type === DELIVERY_TYPE.VOICEMAIL_READ_CONDITION,
    );
    if (hasDeliveryVoiceRead && !values.isVoicemailExtensions) {
      return toast.error(
        "Check voicemail condition can only be use for voicemail extension notifications.",
      );
    }
    toast.info("Saving notification, please wait.", {
      autoClose: 2000,
    });

    const timeout = setTimeout(() => {
      toast.info(DEFAULT_WAIT_MESSAGE, {
        autoClose: 5000,
        transition: Zoom,
      });
    }, 6000);
    setState((prevState) => ({
      ...prevState,
      isSaving: true,
    }));
    const [webhookPayload, webhookHeader] = (
      values.delivery.webhooks || []
    ).reduce(
      (acc, item) => {
        if (Object.keys(item.json || {}).length) {
          acc[0].push({
            ...item.json,
            __webhookAddress: item.webhook,
          });
        }
        if ((item.headers || []).length) {
          item.headers = item.headers.map((header) => ({
            ...header,
            __webhookAddress: item.webhook,
          }));
          acc[1].push(...item.headers);
        }
        return acc;
      },
      [[], []],
    );

    const { data, error } = await Service.editNotification(params.id, {
      name: values.name,
      call_direction: values.callDirection,
      external_phone_number:
        values.isVoicemailExtensions || values.isExtensionNumber
          ? null
          : values.externalNumber,
      extension_id: values.isVoicemailExtensions
        ? values.voicemail_extensions?.id
        : values.isExtensionNumber
          ? values.extensionNumber?.id
          : null,
      extension_number: values.isExtensionNumber
        ? values.extensionNumber?.extensionNumber
        : null,
      voicemail_extension: values.isVoicemailExtensions
        ? values.voicemail_extensions?.extensionNumber ||
          values.voicemail_extensions
        : null,
      timezone: values?.delivery?.selectedTimezone || null,
      is_extension_number: values.isExtensionNumber,
      users: values.switches.users
        ? values.switches.allUsers
          ? "All Users"
          : values.triggers.users.map((user) => user.id).join(",")
        : null,
      extension_patterns: values.switches.extensionPattern
        ? values.triggers.extensionPattern
        : null,
      extension_sites: values.switches.siteExtensions
        ? values.triggers.sites.map((site) => site.name).join(",")
        : null,
      emails: values.switches.sendImmediate
        ? values.switches.email
          ? values.delivery.email
          : null
        : null,
      webhook: values.switches.sendImmediate
        ? values.switches.webhook
          ? values.delivery.webhooks.map(({ webhook }) => webhook).join(",")
          : null
        : null,
      glip_teams: values.switches.sendImmediate
        ? values.switches.ringCentralApp
          ? values.delivery.ringCentralTeams
          : null
        : null,
      ringout_extension: values.switches.sendImmediate
        ? values.switches.ringout
          ? values.delivery.ringout?.extension?.id
          : null
        : null,
      ringout_to_number: values.switches.sendImmediate
        ? values.switches.ringout
          ? values.delivery.ringout?.extensionPhoneNumber?.phoneNumber
          : null
        : null,
      ringout_from_number: values.switches.sendImmediate
        ? values.switches.ringout
          ? values.delivery.ringout?.phoneNumber
          : null
        : null,
      sms_to: values.switches.sendImmediate
        ? values.switches.sms
          ? values.delivery.sms
          : null
        : null,
      sms_from: values.switches.sendImmediate
        ? values.switches.sms
          ? values.delivery.sms_from || null
          : null
        : null,
      message: values.delivery.message,
      payload: values.switches.sendImmediate
        ? values.switches.webhook && webhookPayload.length
          ? webhookPayload
          : null
        : null,
      headers: values.switches.sendImmediate
        ? values.switches.webhook && webhookHeader.length
          ? webhookHeader
          : null
        : null,
      sendImmediate: values.switches.sendImmediate,
      is_immediate: values.switches.sendImmediate,
      delayConfig: (values.deliverySteps || []).map((item) => {
        // if (item.type === "delay" && item.delayUnit === "min") {
        //   item.value = item.value / 60;
        // }
        if (item.type === "delay" && item.delayUnit === "min") {
          delete item.valueParsed;
          return {
            ...item,
            value: item.value * 60,
          };
        }
        if (item.type === "ringout") {
          item.value = {
            ringout_extension:
              item.value?.extension?.id || item.value?.extension,
            ringout_to_number:
              item.value?.extensionPhoneNumber?.phoneNumber ||
              item.value?.extensionPhoneNumber,
            ringout_from_number: item.value?.phoneNumber,
          };
        }
        return item;
      }),
    });
    clearTimeout(timeout);
    setState((prevState) => ({ ...prevState, isSaving: false }));
    if (error) {
      toast.error(
        Array.isArray(error)
          ? error[0] && error[0].message
          : error || DEFAULT_ERROR_MESSAGE,
      );
    } else {
      setState((prevState) => ({
        ...prevState,
        dialog: {
          ...prevState.dialog,
          saved: true,
        },
      }));
    }
  };

  const handleToggle = () => {
    if (!state.preference.toggle) {
      setState((prevState) => ({
        ...prevState,
        dialog: {
          ...prevState.dialog,
          toggle: true,
        },
      }));
    } else {
      handleToggleConfirmation();
    }
  };

  const toggleOnCancel = () => {
    setState((prevState) => ({
      ...prevState,
      dialog: {
        ...prevState.dialog,
        toggle: false,
      },
    }));
  };

  const toggleOnPreferenceChange = (event) => {
    setState((prevState) => {
      const updatedPreferences = {
        ...prevState.preference,
        toggle: event.currentTarget.checked,
      };

      updatePreferences(updatedPreferences);
      return {
        ...prevState,
        preference: updatedPreferences,
      };
    });
  };

  const handleToggleConfirmation = async () => {
    toast.info("Toggling notification.", {
      autoClose: 2000,
    });
    const query = `?ids=${state.notification?.id}`;
    const { error } = await Service.toggle(query, !state.notification?.enabled);
    if (error) {
      toast.error(Array.isArray(error) ? error[0] && error[0].message : error);
    } else {
      toast.success("Toggled successfully");
      setState((prevState) => ({
        ...prevState,
        dialog: {
          ...prevState.dialog,
          toggle: false,
        },
      }));
      fetchNotification();
    }
  };

  const handleNewTeamCreated = (team = {}) => {
    setAppData("teams", [...(appData?.teams || []), team]);
  };

  const fetchUserPreferences = useCallback(async () => {
    const { data, error } = await AppService.getUserDetails();
    if (error) {
      toast.error(Array.isArray(error) ? error[0] && error[0].message : error);
    } else {
      setState((prevState) => ({
        ...prevState,
        preference: {
          delete: data.user.user_preference.delete_notification,
          duplicate: data.user.user_preference.duplicate_notification,
          duplicateSuccess: data.user.user_preference.notification_duplicated,
          bulkEdit: data.user.user_preference.confirm_bulk_edit,
          notificationEdit: data.user.user_preference.notification_edited,
          toggle: data.user.user_preference.toggle_notification,
        },
      }));
    }
  }, []);

  useEffect(() => {
    fetchUserPreferences();
    return () => {
      setState(defaultState);
    };
  }, []);

  if (state.notification === null) {
    return <PageLoader className="ml-8 mt-10" fullPage />;
  }
  return (
    <EditNotificationView
      dialog={state.dialog}
      isSaving={state.isSaving}
      notification={state.notification}
      preference={state.preference}
      userHasChanged={state.userHasChanged}
      values={state.values}
      isParsingPhoneNumber={state.isParsingPhoneNumber}
      isPhoneNumberParsed={state.isPhoneNumberParsed}
      handleDuplicateClick={handleDuplicateClick}
      handleDeleteConfirmation={handleDeleteConfirmation}
      updatePreferences={updatePreferences}
      handleDuplicateConfirmation={handleDuplicateConfirmation}
      isSectionComplete={isSectionComplete}
      handleFormSubmit={handleFormSubmit}
      handleDelete={handleDelete}
      notificationFormOnChange={notificationFormOnChange}
      onParsingPhoneNumber={onParsingPhoneNumber}
      onPhoneNumParsedSuccess={onPhoneNumParsedSuccess}
      deleteOnCancel={deleteOnCancel}
      deleteOnPreferenceChange={deleteOnPreferenceChange}
      duplicateOnCancel={duplicateOnCancel}
      duplicateOnPreferenceChange={duplicateOnPreferenceChange}
      duplicateOnClose={duplicateOnClose}
      DuplicateOnConfirm={DuplicateOnConfirm}
      handleDuplicateSuccess={handleDuplicateSuccess}
      sites={appData.sites}
      users={appData.users}
      teams={appData.teams}
      loadedTeams={appData.loadedTeams}
      loadedSites={appData.loadedSites}
      loadedUsers={appData.loadedUsers}
      loadedCountries={appData.loadedCountries}
      loadedExtension={appData.loadedExtension}
      toggleOnCancel={toggleOnCancel}
      toggleOnPreferenceChange={toggleOnPreferenceChange}
      handleToggleConfirmation={handleToggleConfirmation}
      handleToggle={handleToggle}
      onNewTeamCreated={handleNewTeamCreated}
    />
  );
};

export default EditNotification;
